/**
 * `<GmRoster>` — the content of the GM desktop's **ROSTER.app** window: an overview of
 * **every** pilot in the game (not just one slot's, the way the player {@link PilotPicker}
 * filters by ownership), each with its mechs nested beneath. It's the GM's command-center
 * view of the whole lance.
 *
 * Clicking a pilot head opens that pilot's PILOT page (its sheet, with EDIT); clicking a mech chip opens it in MECH.app; the trailing ＋ mints a fresh mech
 * under that pilot. The GM may drive all of it because the ownership gate is GM ⇒ anything —
 * the editors dispatch the same roster commands a player would, just unfiltered by owner.
 *
 * The page's head is FULL REPAIR SQUAD ({@link SquadRepairBar}), the GM's one-gesture Full Repair
 * of every lancer.
 *
 * Read-only otherwise: every name/stat is read off the live, authoritative roster + computed
 * views threaded in via `roster` (never re-derived). Owner slot names resolve through
 * `slotName` (the Game room's slot list), so a pilot reads as "whose" it is.
 */
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../lib/Icon";
import { listManufacturers } from "@lancer-vtt/compendium";
import type { PilotData } from "@lancer-vtt/domain";
import { useCompendiumContext } from "../../lib/CompendiumProvider";
import { resolveMechArtUrl } from "../../lib/mechArt";
import type { UseRoster } from "../../lib/roster/useRemoteRoster";
import { AvatarImage } from "./PilotAvatar";
import { RosterAvatar } from "./RosterAvatar";
import { SquadRepairBar } from "./SquadRepairBar";

/** Manufacturer id → full brand name, for a mech's chassis sub-line. */
const MFR_NAME = new Map(listManufacturers().map((m) => [m.id, m.name]));

export function GmRoster({
  roster,
  slotName,
  actorId,
  playerSlotIds,
  activeEncounterId,
  onSquadRepaired,
  openPilotId,
  openMechId,
  onOpenPilot,
  onOpenMech,
  onCreateMech,
}: {
  roster: UseRoster;
  /** Owner slot id → display name (`"UNKNOWN"` for an unresolvable slot, never the raw id — see
   *  `useSlotNames`; `"—"` for `null`, an unowned pilot). */
  slotName: (slotId: string | null) => string;
  /** The GM's verified slot id, stamped on FULL REPAIR SQUAD. */
  actorId: string;
  /** The player slots' ids: their pilots are the squad FULL REPAIR SQUAD repairs. */
  playerSlotIds: ReadonlySet<string>;
  /** `session.activeEncounterId`, for FULL REPAIR SQUAD's warning. */
  activeEncounterId: string | null;
  /** FULL REPAIR SQUAD landed: play its banner at the table. */
  onSquadRepaired: (sub: string) => void;
  /** The pilot whose PILOT.app is open (highlighted), or null. */
  openPilotId: string | null;
  /** The mech whose MECH.app is open (highlighted), or null. */
  openMechId: string | null;
  onOpenPilot: (pilotId: string) => void;
  onOpenMech: (pilotId: string, mechId: string) => void;
  onCreateMech: (pilotId: string) => void;
}) {
  const { compendium } = useCompendiumContext();
  // Every pilot in the game, stable id order so the list doesn't reshuffle across snapshots.
  const pilots: PilotData[] = Object.values(roster.state?.pilots ?? {}).sort((a, b) =>
    a.id.localeCompare(b.id),
  );

  if (pilots.length === 0) {
    return (
      <div className="gms-content-message">
        No pilots on file yet. Players build their own from the pilot picker once they log in.
      </div>
    );
  }

  return (
    <div className="gms-roster">
      <SquadRepairBar roster={roster} actorId={actorId} playerSlotIds={playerSlotIds} activeEncounterId={activeEncounterId} onRepaired={onSquadRepaired} />
      {pilots.map((pilot) => {
        const computed = roster.computed[pilot.id];
        return (
          <div key={pilot.id} className="gms-roster-pilot">
            <button
              type="button"
              className={`gms-roster-head${openPilotId === pilot.id ? " open" : ""}`}
              title={`Open ${pilot.callsign || "pilot"}`}
              onClick={() => onOpenPilot(pilot.id)}
            >
              <RosterAvatar pilot={pilot} />
              <span className="gms-roster-head-text">
                <span className="gms-roster-callsign">{(pilot.callsign || "UNNAMED").toUpperCase()}</span>
                <span className="gms-roster-owner">
                  {slotName(pilot.owner)} · LL{pilot.level}
                </span>
              </span>
            </button>

            <div className="gms-roster-mechs">
              {pilot.mechs.map((mech) => {
                const frame = mech.frameId ? compendium.getById("frame", mech.frameId) : undefined;
                const frameName = (frame?.name ?? mech.frameId ?? "").toUpperCase();
                const mfrName = frame?.source ? MFR_NAME.get(frame.source) ?? frame.source : undefined;
                const sub = !mech.frameId
                  ? "NO CHASSIS"
                  : mfrName
                    ? `${frameName} // ${mfrName}`
                    : frameName || "UNREGISTERED CHASSIS";
                const hp = computed?.mechs.find((m) => m.id === mech.id)?.stats.hp;
                const artUrl = resolveMechArtUrl(mech);
                return (
                  <button
                    key={mech.id}
                    type="button"
                    className={`gms-mechtile${openMechId === mech.id ? " open" : ""}`}
                    title={`Configure ${mech.name}`}
                    onClick={() => onOpenMech(pilot.id, mech.id)}
                  >
                    {/* Chassis art — same resolver + degrade-gracefully treatment as the hangar's
                        player-side dock tile (shares `.gms-mechtile-art`). */}
                    {artUrl && (
                      <div className="gms-mechtile-art">
                        <AvatarImage
                          src={artUrl}
                          alt=""
                          className="gms-mechtile-art-img"
                          fallback={<span className="gms-mechtile-art-empty">NO ART</span>}
                        />
                      </div>
                    )}
                    <div className="gms-mechtile-name">{mech.name || "Unnamed mech"}</div>
                    <div className="gms-mechtile-sub">{sub}</div>
                    <div className="gms-mechtile-foot">
                      {typeof hp === "number" && <span className="gms-mechtile-hp">HP {hp}</span>}
                    </div>
                  </button>
                );
              })}
              <button
                type="button"
                className="gms-mechtile gms-mechtile-new"
                title={`Create a new mech for ${pilot.callsign || "this pilot"}`}
                onClick={() => onCreateMech(pilot.id)}
              >
                <Icon icon={faPlus} className="icon-lead" />CREATE MECH
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
